import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { API, graphqlOperation } from 'aws-amplify';
import { getUserSelector } from '../../../../../../redux/reducers/reducerSlices/UserReducer';
import { getAuthReducer } from '../../../../../../redux/reducers/reducerSlices/AuthReducer';
import { showAlert, formatDate_MM_DD_YYYY } from '../../../../../Assests/Utility';
import WarehouseSearchSS from '../../../../SuperWarehouse/Components/SearchWarehouse/WarehouseSearchSS';
import DatePickerSS from '../../../../DatePickerSS/DatePickerSS';
import DmsDashboardHeader from './Components/DmsDashboardHeader';
import './DmsDashboard.css';
import DmsDashboardList from './Components/DmsDashboardList';
import { getDmsBuckets } from '../../../../../../graphql/queries';
import { onPublishDMSTruckData } from '../../../../../../graphql/subscriptions';

const DmsDashboard = (props) => {
    const { themeSelected } = props;
    const dispatch = useDispatch();
    const user = useSelector(getUserSelector);
    const { selectedWarehouse, isOnline } = user;
    const { sessionToken } = useSelector(getAuthReducer);

    const [searchStr, setSearchStr] = useState('');
    const [bucketData, setBucketData] = useState([]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [isLoading, setIsLoading] = useState(false);
    // Holds the latest truck object received from the subscription.
    // DmsDashboardList watches this to apply the change in-place (no full refetch).
    const [latestTruckUpdate, setLatestTruckUpdate] = useState(null);

    const truckSubscriptionRef = useRef(null);
    const isOnlineRef = useRef(isOnline);
    
    // Set min and max dates for date picker (past 7 days to today)
    const minDate = new Date();
    minDate.setDate(minDate.getDate() - 7); // 7 days ago
    const maxDate = new Date(); // Today

    useEffect(() => {
        if (selectedWarehouse?.ID && selectedDate) {
            fetchBucketList();
        }
    }, [selectedWarehouse?.ID, selectedDate]);

    useEffect(() => {
        if (isOnline && sessionToken && selectedWarehouse?.ID && selectedDate) {
            subscribeToUpdates();
        }

        return () => {
            unsubscribeAll();
        };
    }, [isOnline, sessionToken, selectedWarehouse?.ID, selectedDate]);

    useEffect(() => {
        isOnlineRef.current = isOnline;
    }, [isOnline]);

    const fetchBucketList = async () => {
        try {
            setIsLoading(true);
            const formattedDate = moment(selectedDate).format('YYYY-MM-DD');

            const response = await API.graphql(
                graphqlOperation(getDmsBuckets, {
                    IDWarehouse: selectedWarehouse?.ID || '',
                    selectedDate: formattedDate
                })
            );

            const buckets = response?.data?.getDmsBuckets || [];
            setBucketData(buckets);
            console.log('✅ DMS Buckets fetched:', buckets.length);
        } catch (error) {
            console.error('❌ Error fetching DMS buckets:', error);
            showAlert(toast.TYPE.ERROR, 'Failed to fetch DMS buckets', themeSelected);
            setBucketData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const subscribeToUpdates = () => {
        // Clean up existing subscription
        unsubscribeAll();

        const formattedDate = moment(selectedDate).format('YYYY-MM-DD');

        // Single subscription: onPublishDMSTruckData (IDWarehouse only — no selectedDate per schema)
        console.log('🔔 Setting up DMS truck subscription for warehouse:', selectedWarehouse?.ID, 'date:', formattedDate);

        truckSubscriptionRef.current = API.graphql({
            ...graphqlOperation(onPublishDMSTruckData, {
                IDWarehouse: selectedWarehouse?.ID
            }),
            authMode: process.env.REACT_APP_APPSYNC_AUTHENTICATIONTYPE
        }).subscribe({
            next: ({ value }) => {
                const truckData = value?.data?.onPublishDMSTruckData;
                console.log('🚚 DMS Truck update received:', truckData);

                if (!truckData) return;

                // Client-side date filter: only react to trucks on the currently selected date
                // if (truckData.ScheduledTime) {
                //     const truckDate = moment(truckData.ScheduledTime).format('YYYY-MM-DD');
                //     if (truckDate !== formattedDate) {
                //         console.log('⏭️ Skipping truck update - date mismatch:', truckDate, 'vs', formattedDate);
                //         return;
                //     }
                // }

                // 1. Refetch getDmsBuckets so header counts stay current
                fetchBucketList();

                // 2. Pass the exact changed truck to DmsDashboardList to update in-place
                setLatestTruckUpdate(truckData);
            },
            error: (error) => {
                console.warn('❌ DMS Truck subscription error:', error);
                setTimeout(() => {
                    if (isOnlineRef.current && selectedWarehouse?.ID) {
                        console.log('🔄 Attempting to resubscribe to truck updates...');
                        subscribeToUpdates();
                    }
                }, 5000);
            }
        });
    };

    const unsubscribeAll = () => {
        if (truckSubscriptionRef.current) {
            truckSubscriptionRef.current.unsubscribe();
            truckSubscriptionRef.current = null;
        }
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    return (
        <div className="container-fluid dms-dashboard-container">
            <div className='dms-dashboard-top-bar d-flex py-1 text-start justify-content-between align-items-center'>
                
                <div className='d-flex gap-3 align-items-center'>
                    {/* <div className='dms-dashboard-title'>DMS</div> */}
                
                     <DmsDashboardHeader 
                bucketData={bucketData}
                themeSelected={themeSelected}
            />
                    <div className="pos-rel">
                        <button
                            className="datePickerBtn"
                            onClick={() => setShowDatePicker(!showDatePicker)}
                        >
                            <span className="btn-title">
                                {selectedDate ? formatDate_MM_DD_YYYY(selectedDate) : moment().format('MM/DD/YYYY')}
                            </span>
                            <i className="fa-regular fa-calendar ms-2"></i>
                        </button>
                        {showDatePicker && (
                            <div className="dms-dashboard-datepicker">
                                <DatePickerSS
                                    setSelectedDate={handleDateChange}
                                    selectedDate={selectedDate}
                                    closeCalendar={() => setShowDatePicker(false)}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                />
                            </div>
                        )}
                    </div>
                    
                    {/* <div className="dms-dashboard-search-wrapper">
                        <WarehouseSearchSS
                            placeholder='Search...'
                            inputValue={searchStr}
                            cancelSearch={() => setSearchStr('')}
                            onChangeHandler={(text) => setSearchStr(text)}
                        />
                    </div> */}
                </div>
            </div>

           

            <div className="dmsDashboardTableContainer">
                <DmsDashboardList
                    bucketList={bucketData}
                    searchStr={searchStr}
                    themeSelected={themeSelected}
                    loading={isLoading}
                    selectedDate={selectedDate}
                    latestTruckUpdate={latestTruckUpdate}
                />
            </div>
        </div>
    );
};

export default DmsDashboard;